import React, { useMemo, useState, useEffect, useRef } from 'react';
import { alpha, createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { DataGrid, gridClasses, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { Box } from '@mui/material';
import './dataGrid.scss';

const ODD_OPACITY = 0.2;
const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity,
        ),
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'hidden'
  },
  '& .MuiDataGrid-columnHeaders': {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '#636A73',
    '& .MuiDataGrid-filler': {
      backgroundColor: '#636A73'
    },
    '& .MuiDataGrid-scrollbarFiller': {
      backgroundColor: 'transparent'
    }
  },
  '&& .MuiTablePagination-root .MuiTablePagination-input': {
    display: 'inline-flex !important'
  },
}));

export default function DataGridGenerico({
  columns = [],
  rows,
  toolbar = true,
  fileNameExport = 'registros',
  localeText,
  onRowClick = null,
  hideFooterPagination = false,
  hideFooterSelectedRowCount = false,
  checkboxSelection = false,
  disabledSelectionAll = false,
  keepNonExistentRowsSelected = false,
  onRowSelectionModelChange = null,
  rowSelectionModel,
  pageSize,
  onPageChange,
  onPageSizeChange,
  totalCount = 0,
  currentPage,
  loading = false,
  sortingOrder = ['ASC', 'DESC'],
  sortingMode = 'client',
  onSortModelChange = () => { },
  sortModel,
  hideFooter = false,
  rowsPerPage,
  mobileTable,
  headerHeight = 27,
  rowHeight = 40,
  isRowSelectable,
  autoMaxWidth = false,
  maxWidth = 0,
  initialState,
  entityId,
  isRowSelected,
  disableRowSelectionOnClick = false,
  getRowClassName,
  getRowHeight,
  summaryRow = null,
  maxHeight = null
}) {

  const paginationMode = pageSize ? 'server' : 'client';
  const [clientPageSize, setClientPageSize] = useState(10);
  const [clientPage, setClientPage] = useState(0);
  const theme = createTheme(esES, esES);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { adjustedColumns, maxWidthSize } = useMemo(() => {
    const checkboxSelectionSize = checkboxSelection ? 30 : 0;

    if (!autoMaxWidth && !maxWidth) {
      return { adjustedColumns: columns, maxWidthSize: null };
    }

    const originalColumns = columns;
    const totalColumnsWidth = originalColumns.reduce((total, column) => total + (column?.width || 0), 0);

    if (maxWidth || autoMaxWidth) {
      const maxWidthWithCheckbox = maxWidth + checkboxSelectionSize;

      if (maxWidthWithCheckbox < screenWidth) {
        const availableWidth = screenWidth - checkboxSelectionSize - 44;
        const scaleFactor = availableWidth / totalColumnsWidth;

        const adjustedColumns = scaleFactor >= 1.8
          ? originalColumns.map(column => ({
            ...column,
            width: column?.width ? Math.floor((column.width / totalColumnsWidth) * (availableWidth - totalColumnsWidth)) : column.width
          }))
          : originalColumns.map(column => ({
            ...column,
            width: column?.width ? Math.floor(column.width * scaleFactor) : column.width
          }));

        return { adjustedColumns, maxWidthSize: '100%' };
      }

      return { adjustedColumns: originalColumns, maxWidthSize: maxWidthWithCheckbox };
    }

    return { adjustedColumns: originalColumns, maxWidthSize: totalColumnsWidth + checkboxSelectionSize };
  }, [maxWidth, autoMaxWidth, checkboxSelection, screenWidth, columns]);

  const CustomToolbar = () => {
    if (toolbar) {
      return (
        <GridToolbarContainer>
          <GridToolbarExport
            printOptions={{ disableToolbarButton: true }}
            csvOptions={{
              fileName: fileNameExport,
              delimiter: ';',
              utf8WithBom: true,
            }}
          />
        </GridToolbarContainer>
      );
    }
  };

  const internalSelectionModel = useMemo(() => ({
    type: 'include',
    ids: new Set(rowSelectionModel ?? [])
  }), [rowSelectionModel]);

  const handleRowSelectionModelChange = (selection) => {
    const idsArray = Array.from(selection?.ids ?? []);
    onRowSelectionModelChange?.(idsArray);
  };

  const processedRows = useMemo(() => {
    if (!rows || !Array.isArray(rows)) return [];

    const mapped = rows.map((item, index) => ({
      id: entityId ? item[entityId] : index + 1,
      ...item,
    }));

    return mapped;
  }, [rows, entityId]);

  const rowCount = useMemo(() => {
    // El rowCount del server-side no debe incluir la fila de resumen
    const dataRows = summaryRow ? processedRows.length - 1 : processedRows.length;
    return paginationMode === 'server' ? (totalCount || dataRows) : processedRows.length;
  }, [paginationMode, totalCount, processedRows, summaryRow]);

  const resolveRowClassName = (params) => {
    if (getRowClassName) return getRowClassName(params);
    return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
  };

  return (
    <ThemeProvider theme={theme}>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <StripedDataGrid
          className={`data-grid ${mobileTable ? 'left-pagination' : ''} ${disabledSelectionAll && checkboxSelection ? 'disabled-selection-all' : ''} ${summaryRow ? 'has-summary-row' : ''}`}
          rowHeight={rowHeight}
          components={{ Toolbar: CustomToolbar }}
          disableColumnMenu
          loading={loading}
          columns={adjustedColumns}
          disableRowSelectionExcludeModel
          density="standard"
          columnHeaderHeight={headerHeight}
          disableSelectionOnClick={true}
          localeText={{ ...esES.components.MuiDataGrid.defaultProps.localeText, ...localeText }}
          pagination
          getRowClassName={resolveRowClassName}
          rows={processedRows}
          pageSizeOptions={rowsPerPage || [10, 25, 50]}
          onRowClick={onRowClick}
          sx={{
            backgroundColor: 'white',
            '& .MuiDataGrid-panelFooter': { fontSize: '10pt' },
            ".MuiDataGrid-columnHeaderTitleContainer": {
              ".MuiSvgIcon-root": { color: "white" },
            },
            maxWidth: !!maxWidthSize ? (maxWidthSize + 22) : '100%',
            ...(maxHeight != null && { maxHeight }),
          }}
          hideFooterPagination={hideFooterPagination}
          hideFooterSelectedRowCount={hideFooterSelectedRowCount}
          hideFooter={hideFooter}
          checkboxSelection={checkboxSelection}
          keepNonExistentRowsSelected={keepNonExistentRowsSelected}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          rowSelectionModel={internalSelectionModel}
          onPaginationModelChange={(models) => {
            onPageSizeChange ? onPageSizeChange(models.pageSize) : setClientPageSize(models.pageSize);
            onPageChange ? onPageChange(models.page) : setClientPage(models.page);
          }}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          paginationModel={{
            pageSize: pageSize ? pageSize : (hideFooter ? processedRows?.length || 10 : clientPageSize),
            page: currentPage ? currentPage : clientPage
          }}
          rowCount={rowCount}
          sortingOrder={sortingOrder?.map(so => so.toLowerCase())}
          onSortModelChange={onSortModelChange}
          {...(sortModel && { sortModel })}
          paginationMode={paginationMode}
          isRowSelectable={isRowSelectable}
          isRowSelected={isRowSelected}
          initialState={{
            ...initialState,
            pagination: {
              paginationModel: {
                pageSize: hideFooter ? processedRows?.length || 10 : 10,
                page: 0
              },
            },
          }}
          sortingMode={sortingMode}
          getRowHeight={getRowHeight}
        />
        {summaryRow && <Box sx={{
          display: 'flex',
          width: '100%',
          maxWidth: '100%',
          justifyContent: 'flex-end',
          background: '#dedede',
          fontWeight: 'bold',
          py: '6px',
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}>
          <Box sx={{ pr: 1 }}>
            {summaryRow.key || 'Total'}: {summaryRow.value}
          </Box>
        </Box>}
      </Box>
    </ThemeProvider >
  );
}